import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { AlertTriangle, ChevronLeft } from 'lucide-react';
import {
  Button,
  Card,
  CardHeader,
  Input,
  ProviderLogo,
  ScopeChipsInput,
  type ScopeChipsInputHandle,
} from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { routes } from '@/lib/routes';
import {
  useCreateOrgMcpServer,
  useOrgMcpServer,
  useUpdateOrgMcpServer,
} from '@/hooks/useOrgMcpServers';
import {
  useMcpProviderById,
  useMcpProviders,
} from '@/hooks/useMcpProviders';
import { McpProviderPicker } from '../tools/mcp/McpProviderPicker';
import {
  McpCatalogFormFields,
  resolveTemplateUrl,
} from '../tools/mcp/McpCatalogFormFields';
import {
  McpAuthMethodChooser,
  sortAuthMethods,
} from '../tools/mcp/McpAuthMethodChooser';
import type {
  CreateOrgMcpServerInput,
  McpAuthMethod,
  McpCreateErrorCode,
  UpdateOrgMcpServerInput,
} from '@/types/mcp';

function errorCode(err: unknown): string | undefined {
  if (err && typeof err === 'object' && 'code' in err) {
    const c = (err as { code?: unknown }).code;
    return typeof c === 'string' ? c : undefined;
  }
  return undefined;
}

function isHttpsUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

export default function OrgMcpServerForm() {
  const navigate = useNavigate();
  const { serverId } = useParams<{ serverId?: string }>();
  const isEditing = !!serverId;
  const { success, error: showError } = useToast();

  const createServer = useCreateOrgMcpServer();
  const updateServer = useUpdateOrgMcpServer();
  const { data: existing, isLoading: existingLoading } = useOrgMcpServer(serverId);

  const [searchParams, setSearchParams] = useSearchParams();
  const providerIdParam = searchParams.get('provider');
  const isCustom = providerIdParam === 'custom';
  const { data: catalog, isLoading: catalogLoading } = useMcpProviders();
  const provider = useMcpProviderById(
    !isCustom && providerIdParam ? providerIdParam : undefined,
  );

  const [name, setName] = useState('');
  const [serverUrl, setServerUrl] = useState('');
  const [defaultGroup, setDefaultGroup] = useState('');
  const [scopes, setScopes] = useState<string[]>([]);
  const scopesRef = useRef<ScopeChipsInputHandle>(null);

  // Manual-client credentials when the catalog flow requires them; kept in
  // these locals alongside the catalog form so the same submit path can pick
  // them up via the form's `manualClient` field.
  const [manualClientId, setManualClientId] = useState('');
  const [manualClientSecret, setManualClientSecret] = useState('');

  // Service-account credential (paste-only — no OAuth dance for the SA itself).
  const [saClientId, setSaClientId] = useState('');
  const [saClientSecret, setSaClientSecret] = useState('');
  const [saRefreshToken, setSaRefreshToken] = useState('');
  const [saScopes, setSaScopes] = useState<string[]>([]);
  const saScopesRef = useRef<ScopeChipsInputHandle>(null);

  const [errorCodeState, setErrorCodeState] =
    useState<McpCreateErrorCode | null>(null);
  const [urlError, setUrlError] = useState<string | undefined>(undefined);

  // Catalog-driven state. `seededFor` keys the seed effect by providerId so
  // switching providers re-seeds rather than carrying stale values.
  const [templateValues, setTemplateValues] = useState<Record<string, string>>(
    {},
  );
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [headerValues, setHeaderValues] = useState<Record<string, string>>({});
  const [selectedMethodKind, setSelectedMethodKind] =
    useState<McpAuthMethod['kind'] | null>(null);
  const [seededFor, setSeededFor] = useState<string | null>(null);

  // Canonical UI order — OAuth → API → Manual OAuth → unknown. Drives both
  // the chooser's render order AND the form's default-selected method.
  const sortedMethods = useMemo(
    () => (provider ? sortAuthMethods(provider.authMethods) : []),
    [provider],
  );

  const selectedMethod: McpAuthMethod | null =
    sortedMethods.length > 0
      ? sortedMethods.find((m) => m.kind === selectedMethodKind) ??
        sortedMethods[0] ??
        null
      : null;

  useEffect(() => {
    if (!isEditing || !existing) return;
    setName(existing.name);
    setServerUrl(existing.serverUrl);
    setDefaultGroup(existing.defaultGroup ?? '');
    setScopes(existing.scopes ?? []);
    setSaScopes(existing.serviceAccount?.scopes ?? []);
  }, [isEditing, existing]);

  useEffect(() => {
    if (!provider) return;
    if (seededFor === provider.providerId) return;
    setName(provider.displayName);
    setDefaultGroup(provider.defaultGroup);
    // Default to the first method in canonical UI order, not catalog order.
    const firstMethod = sortAuthMethods(provider.authMethods)[0] ?? null;
    setSelectedMethodKind(firstMethod ? firstMethod.kind : null);
    // `'scopes' in m` narrows away api-key / oauth-manual (neither carries
    // a scope spec) so we only seed when the OAuth method has a full picker.
    if (
      firstMethod &&
      'scopes' in firstMethod &&
      firstMethod.scopes?.available &&
      firstMethod.scopes.available.length > 0
    ) {
      setSelectedScopes([...firstMethod.scopes.recommended]);
    } else {
      setSelectedScopes([]);
    }
    if (typeof provider.serverUrl !== 'string') {
      const seeded: Record<string, string> = {};
      for (const v of provider.serverUrl.variables) {
        if (v.default) seeded[v.name] = v.default;
      }
      setTemplateValues(seeded);
    } else {
      setTemplateValues({});
    }
    setManualClientId('');
    setManualClientSecret('');
    setHeaderValues({});
    setSeededFor(provider.providerId);
  }, [provider, seededFor]);

  useEffect(() => {
    if (!provider || !selectedMethod) return;
    if (selectedMethod.kind === 'api-key') {
      setSelectedScopes([]);
      setManualClientId('');
      setManualClientSecret('');
    } else {
      setHeaderValues({});
      const scopes = 'scopes' in selectedMethod ? selectedMethod.scopes : undefined;
      if (scopes?.available && scopes.available.length > 0) {
        setSelectedScopes([...scopes.recommended]);
      } else {
        setSelectedScopes([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMethod?.kind]);

  if (isEditing && existingLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  const submitting = createServer.isPending || updateServer.isPending;

  const resolvedUrl = provider
    ? resolveTemplateUrl(provider, templateValues)
    : serverUrl.trim();

  const requiresManualClient = selectedMethod?.kind === 'oauth-manual-client';
  const manualClientReady = requiresManualClient
    ? manualClientId.trim().length > 0 && manualClientSecret.trim().length > 0
    : true;
  const apiKeyReady =
    selectedMethod?.kind === 'api-key'
      ? selectedMethod.headers.every(
          (h) => (headerValues[h.name] ?? '').trim().length > 0,
        )
      : true;

  const canSubmit =
    name.trim().length > 0 &&
    resolvedUrl.length > 0 &&
    !urlError &&
    manualClientReady &&
    apiKeyReady;
  const saFilled =
    saClientId.trim().length > 0 && saClientSecret.trim().length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Commit scopes the user typed but didn't press Enter on.
    scopesRef.current?.flush();
    saScopesRef.current?.flush();

    if (!isHttpsUrl(resolvedUrl)) {
      setUrlError('Must be a valid https:// URL');
      return;
    }
    setUrlError(undefined);
    setErrorCodeState(null);

    try {
      if (isEditing && serverId) {
        // PUT on /mcp-servers/{id} updates the server record. The SA credential
        // is managed separately via the detail page's "Replace credential"
        // modal — it isn't part of the update body.
        const updateInput: UpdateOrgMcpServerInput = {
          name: name.trim(),
          serverUrl: resolvedUrl,
          defaultGroup: defaultGroup.trim() || undefined,
          scopes: scopes.length > 0 ? scopes : undefined,
        };
        await updateServer.mutateAsync({ serverId, input: updateInput });
        success('Organization MCP server updated');
        navigate(routes.console.org.mcpServer(serverId));
      } else {
        const isApiKey = selectedMethod?.kind === 'api-key';

        // The catalog-driven manual-client credentials are an OAuth client
        // for the per-user flow — they belong under `manualClient`, NOT
        // `serviceAccount`. SA payloads carry a refresh token and go through
        // a dedicated endpoint (handled in OrgMcpServerDetail's "Replace
        // credential" modal), or are passed on initial install via the
        // separate `serviceAccount` field below for non-catalog flows.
        const manualClient =
          !isApiKey && requiresManualClient && manualClientReady
            ? {
                clientId: manualClientId.trim(),
                clientSecret: manualClientSecret.trim(),
              }
            : undefined;

        // The SA fallback only applies to OAuth installs — api-key installs
        // already store a shared credential at the chosen tier.
        const serviceAccount =
          !isApiKey && !provider && saFilled
            ? {
                clientId: saClientId.trim(),
                clientSecret: saClientSecret.trim(),
                refreshToken: saRefreshToken.trim() || undefined,
                scopes: saScopes.length > 0 ? saScopes : undefined,
              }
            : undefined;

        let createInput: CreateOrgMcpServerInput;
        if (selectedMethod?.kind === 'api-key') {
          createInput = {
            name: name.trim(),
            serverUrl: resolvedUrl,
            defaultGroup:
              defaultGroup.trim() || provider?.defaultGroup || undefined,
            providerId: provider?.providerId,
            authMode: 'api_key',
            headers: Object.fromEntries(
              selectedMethod.headers.map((h) => [
                h.name,
                (headerValues[h.name] ?? '').trim(),
              ]),
            ),
          };
        } else if (selectedMethod?.kind === 'oauth-paste-token') {
          // No-provisioning path: skip OAuth discovery; token is pasted
          // afterwards via the identity oauth-manual endpoint.
          createInput = {
            name: name.trim(),
            serverUrl: resolvedUrl,
            defaultGroup:
              defaultGroup.trim() || provider?.defaultGroup || undefined,
            providerId: provider?.providerId,
            authMode: 'oauth_manual',
          };
        } else {
          createInput = {
            name: name.trim(),
            serverUrl: resolvedUrl,
            defaultGroup:
              defaultGroup.trim() || provider?.defaultGroup || undefined,
            // Empty catalog selection → omit so the backend can fall back to
            // OAuth-time scope discovery.
            scopes: provider
              ? selectedScopes.length > 0
                ? selectedScopes
                : undefined
              : scopes.length > 0
                ? scopes
                : undefined,
            manualClient,
            serviceAccount,
            providerId: provider?.providerId,
          };
        }
        const created = await createServer.mutateAsync(createInput);
        success('Organization MCP server created');
        navigate(routes.console.org.mcpServer(created.serverId));
      }
    } catch (err) {
      const code = errorCode(err);
      if (
        code === 'pkce_unsupported' ||
        code === 'client_required' ||
        code === 'dcr_unsupported'
      ) {
        setErrorCodeState(code as McpCreateErrorCode);
        return;
      }
      showError(err instanceof Error ? err.message : 'Failed to save server');
    }
  };

  if (!isEditing && providerIdParam === null) {
    return (
      <div className="space-y-6 max-w-4xl">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Add organization MCP server
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Install at the organization level — available automatically in
            every space. Pick a provider, or connect a custom MCP-compatible
            endpoint.
          </p>
        </div>
        {catalogLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="h-36 rounded-lg border border-border bg-card animate-pulse"
              />
            ))}
          </div>
        ) : (
          <McpProviderPicker
            providers={catalog?.providers ?? []}
            customServerAllowed={catalog?.customServerAllowed ?? true}
            onSelectProvider={(id) => setSearchParams({ provider: id })}
            onSelectCustom={() => setSearchParams({ provider: 'custom' })}
          />
        )}
      </div>
    );
  }

  const headerTitle = isEditing
    ? 'Edit organization MCP server'
    : provider
      ? `Add ${provider.displayName} (organization)`
      : isCustom
        ? 'Add custom organization MCP server'
        : 'Add organization MCP server';

  return (
    <div className="space-y-6 max-w-3xl">
      {!isEditing && (
        <button
          type="button"
          onClick={() => setSearchParams({})}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" /> Choose a different provider
        </button>
      )}
      <div className="flex items-center gap-4">
        {provider && (
          <ProviderLogo
            slug={provider.providerId}
            displayName={provider.displayName}
            className="h-12 w-12"
            fallbackClassName="text-sm"
          />
        )}
        <div>
          <h1 className="text-2xl font-bold text-foreground">{headerTitle}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Install an MCP server at the organization level. It's automatically
            available in every space — a space-level install of the same vendor
            takes precedence in that space.
          </p>
        </div>
      </div>

      {errorCodeState === 'pkce_unsupported' && (
        <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/20 p-4 flex gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800 dark:text-red-200">
              OAuth provider not supported
            </p>
            <p className="text-sm text-red-700 dark:text-red-300 mt-1">
              This server's OAuth provider doesn't support PKCE and can't be
              used. Check whether the provider exposes a PKCE-compatible
              authorization endpoint, or try a different server.
            </p>
          </div>
        </div>
      )}

      {(errorCodeState === 'client_required' ||
        errorCodeState === 'dcr_unsupported') && (
        <div className="rounded-lg border border-yellow-300 bg-yellow-50 dark:border-yellow-700 dark:bg-yellow-900/20 p-4 flex gap-3">
          <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
              Service-account credential required
            </p>
            <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
              This server doesn't support Dynamic Client Registration. Register
              an OAuth client on the server and paste its credentials in the
              "Service account credential" section below.
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader
            title="Basics"
            description="How this install shows up in the organization catalog."
          />
          <div className="space-y-4">
            <Input
              label="Name"
              value={name}
              onChange={(e: { target: { value: string } }) => setName(e.target.value)}
              placeholder="e.g. Slack MCP"
              required
            />
            <Input
              label="Default group"
              value={defaultGroup}
              onChange={(e: { target: { value: string } }) =>
                setDefaultGroup(e.target.value)
              }
              placeholder="Optional — group label applied to synced tools"
              hint="Tools imported from this server will land under this group in the tool catalog."
            />
          </div>
        </Card>

        {provider && selectedMethod ? (
          <>
            {sortedMethods.length > 1 && (
              <McpAuthMethodChooser
                methods={sortedMethods}
                selectedKind={selectedMethod.kind}
                onChange={setSelectedMethodKind}
              />
            )}
            <McpCatalogFormFields
              provider={provider}
              method={selectedMethod}
              ourCallbackUrl={catalog?.ourCallbackUrl ?? ''}
              templateValues={templateValues}
              setTemplateValues={setTemplateValues}
              selectedScopes={selectedScopes}
              setSelectedScopes={setSelectedScopes}
              manualClientId={manualClientId}
              setManualClientId={setManualClientId}
              manualClientSecret={manualClientSecret}
              setManualClientSecret={setManualClientSecret}
              headerValues={headerValues}
              setHeaderValues={setHeaderValues}
            />
          </>
        ) : provider ? null : (
          <Card>
            <CardHeader
              title="Server"
              description="Connection details. The URL must be an https endpoint."
            />
            <div className="space-y-4">
              <Input
                label="Server URL"
                value={serverUrl}
                onChange={(e: { target: { value: string } }) => {
                  setServerUrl(e.target.value);
                  if (urlError) setUrlError(undefined);
                }}
                placeholder="https://mcp.example.com"
                error={urlError}
                required
              />
              <ScopeChipsInput
                ref={scopesRef}
                label="Scopes"
                value={scopes}
                onChange={setScopes}
                placeholder="Optional — type a scope and press Enter"
                hint="OAuth scopes to request during user consent."
                suggestions={[
                  {
                    value: 'offline_access',
                    hint: 'offline_access keeps the connection alive — without it, it expires with the access token (often an hour) and each member has to reconnect.',
                  },
                ]}
              />
            </div>
          </Card>
        )}

        {!isEditing && !provider && (
          <Card>
            <CardHeader
              title="Service account credential"
              description="Optional. The organization-level fallback used when a calling user has no personal token for this install. Leave blank to use per-user OAuth only."
            />
            <div className="space-y-4">
              <Input
                label="Client ID"
                value={saClientId}
                onChange={(e: { target: { value: string } }) =>
                  setSaClientId(e.target.value)
                }
                placeholder="OAuth client id"
                autoComplete="off"
              />
              <Input
                label="Client secret"
                type="password"
                value={saClientSecret}
                onChange={(e: { target: { value: string } }) =>
                  setSaClientSecret(e.target.value)
                }
                placeholder="OAuth client secret"
                autoComplete="new-password"
              />
              <Input
                label="Refresh token"
                type="password"
                value={saRefreshToken}
                onChange={(e: { target: { value: string } }) =>
                  setSaRefreshToken(e.target.value)
                }
                placeholder="Optional"
                autoComplete="new-password"
                hint="Provide if the SA's access shouldn't depend on user OAuth."
              />
              <ScopeChipsInput
                ref={saScopesRef}
                label="Scopes"
                value={saScopes}
                onChange={setSaScopes}
                placeholder="Optional — type a scope and press Enter"
                hint="Scopes the SA token should carry."
              />
            </div>
          </Card>
        )}

        {isEditing && (
          <Card>
            <CardHeader
              title="Service account credential"
              description="Manage the organization service-account credential from the server detail page."
            />
            <p className="text-sm text-muted-foreground">
              The service-account credential is set via the "Replace credential"
              action on the server detail page.
            </p>
          </Card>
        )}

        <div className="flex justify-end gap-4">
          <Link
            to={
              isEditing && serverId
                ? routes.console.org.mcpServer(serverId)
                : routes.console.org.mcpServers
            }
          >
            <Button type="button" variant="secondary">
              Cancel
            </Button>
          </Link>
          <Button type="submit" isLoading={submitting} disabled={!canSubmit}>
            {isEditing ? 'Save changes' : 'Add MCP server'}
          </Button>
        </div>
      </form>
    </div>
  );
}
